import React, { useState, useEffect, useRef, Fragment } from "react";
import * as httpService from "../services/httpService";
import * as userService from "../utils/userService";
import { Employee_Api_EndPoints } from "../global/end-points";
import LoadingSpinner from "../components/common/loadingSpinner";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom"; 
import useActivityLogger from "../utils/useActivityLogger"; 

function EmployeeTimesheet() {
    const { t } = useTranslation();
    const didMount = useRef(false);
    const navigate = useNavigate();

    const timesheetInfo = {
        employeeTimeSheets: [],
        totalHours: 0,
        taxdeductibleBalance: 0,
        transferredAmount: 0,
        grossPay: 0,
        netPay: 0,
        payPeriod: "",
        totalWages: 0,
        totalTips: 0,
    };

    const [isLoad, setIsLoad] = useState(false);
    const [employeeInfo, setEmployeeTimesheet] = useState(timesheetInfo);

    const effectiveUserId =
        sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");

    const isOBO = !!sessionStorage.getItem("OnBehalfUserId");

    const { logActivity, logClick, logApiCall } = useActivityLogger({
        moduleCode: isOBO ? "DPY" : "DPY",
    });

    useEffect(() => {
        logActivity({
            activity: "PAGE_VIEW",
            description: `${isOBO ? "OBO-" : ""}EmployeeTimesheet Page View`,
            component: `${isOBO ? "OBO." : ""}Page.EmployeeTimesheet`,
        });

        if (!didMount.current) {
            getTimesheets();
        }

        return () => {
            didMount.current = true;
        };
    }, []);

    const getTimesheets = () => {
        if (effectiveUserId) {
            const body = {
                UserId: Number(effectiveUserId),
                LocationId: 1,
            };

            logApiCall(
                `${isOBO ? "OBO.API.GetTimesheets" : "DPY.API.GetTimesheets"}`,
                `${isOBO ? "OBO-" : ""}Get Timesheets (request)`
            );

            httpService
                .Get(Employee_Api_EndPoints.GENERATE_TOTAL_TIMESHEET, body)
                .then(
                    (resp) => {
                        if (resp.status === 200) {
                            setEmployeeTimesheet(resp.data);

                            logActivity({
                                activity: "API_CALL",
                                description: `${isOBO ? "OBO-" : ""}Get Timesheets (success)`,
                                component: `${isOBO ? "OBO." : ""}API.GetTimesheets`,
                            });
                        } else {
                            logActivity({
                                activity: "API_CALL",
                                description: `${isOBO ? "OBO-" : ""}Get Timesheets (error)`,
                                component: `${isOBO ? "OBO." : ""}API.GetTimesheets`,
                            });
                        }
                        setIsLoad(true);
                    },
                    () => {
                        logActivity({
                            activity: "API_CALL",
                            description: `${isOBO ? "OBO-" : ""}Get Timesheets (error)`,
                            component: `${isOBO ? "OBO." : ""}API.GetTimesheets`,
                        });
                        setIsLoad(true);
                    }
                );
        } else {
            setIsLoad(true);
            userService.ClearCookies();
        }
    };

    // ✅ Handle Back button click
    const handleBackClick = async (e) => {
        e.preventDefault();
        await logClick(
            `${isOBO ? "OBO.Button.Back" : "DPY.Button.Back"}`,
            `${isOBO ? "OBO-" : ""}Clicked Back`
        );
        navigate("/doughsummary");
    };

    return (
        <Fragment>
            {isLoad ? (
                <div className="dashboard-container">
                    {/* ✅ Back button with logging */}
                    <Link to="/doughsummary" className="back-link" onClick={handleBackClick}>
                        <i className="arrow left"></i> {t("backbutton")}
                    </Link>

                    <div className="table-container">
                        <h2 className="page-module-heading mb-15">{t("TimeSht")}</h2>
                        <table className="pay-info-table">
                            <thead>
                                <tr>
                                    <th>{t("PayPer")}</th>
                                    <th>{t("Type")}</th>
                                    <th>{t("Hours")}</th>
                                    <th>{t("Pay")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="centered-cell" rowSpan={3}>
                                        {employeeInfo.payPeriod}
                                    </td>
                                    <td className="centered-cell">Eligible Wages</td>
                                    <td className="centered-cell">{employeeInfo.totalHours}</td>
                                    <td className="centered-cell">
                                        {employeeInfo.totalWages.toFixed(2)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="centered-cell">Tips</td>
                                    <td className="centered-cell">-</td>
                                    <td className="centered-cell">
                                        {employeeInfo.totalTips.toFixed(2)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="centered-cell">
                                        <strong>Total</strong>
                                    </td>
                                    <td className="centered-cell"></td>
                                    <td className="centered-cell">
                                        <strong>
                                            {(employeeInfo.totalWages +
                                                employeeInfo.totalTips).toFixed(2)}
                                        </strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="table-container mt-40">
                        <table className="tax-info-table">
                            <tbody>
                                <tr>
                                    <td>{t("EstimatedDeduct")} ({employeeInfo.taxPercentage}%)</td>
                                    <td></td>
                                    <td></td>
                                    <td>${employeeInfo.taxdeductibleBalance.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td>{t("TranPaPer")}</td>
                                    <td></td>
                                    <td></td>
                                    <td>${employeeInfo.transferredAmount.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td>{t("NetAvai")} </td>
                                    <td></td>
                                    <td></td>
                                    <td>${employeeInfo.netPay.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <LoadingSpinner />
            )}
        </Fragment>
    );
}

export default EmployeeTimesheet;
